import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layers, Image, Users, Heart } from 'lucide-react';
import BackLink from '../components/BackLink';
import { SkeletonCard, EmptyState } from '../components/Skeleton';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';
import formatEventDate from '../lib/formatEventDate';
import { supportsCapability } from '../lib/capabilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function OrgDashboardPage() {
  const { user } = useAuth();
  const [orgId, setOrgId] = useState(null);
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        // First find user's org
        const orgRes = await axios.get(`${API_URL}/api/orgs/mine`).catch(() => null);
        const id = orgRes?.data?.id;
        if (!id) { setErr('No organization linked to your account.'); setLoading(false); return; }
        setOrgId(id);
        const r = await axios.get(`${API_URL}/api/orgs/${id}/dashboard`);
        setData(r.data);
        const gRes = await axios.get(`${API_URL}/api/aura-groups`).catch(() => null);
        if (gRes?.data && Array.isArray(gRes.data)) setGroups(gRes.data);
      } catch (e) {
        setErr('Failed to load dashboard.');
      } finally { setLoading(false); }
    };
    if (user) load();
  }, [user]);

  if (loading) return (
    <div className="min-h-screen bg-midnight text-text-light">
      <SiteHeader />
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0,1,2,3].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
      <FooterExpanded />
    </div>
  );
  if (err) return <div className="min-h-screen bg-midnight flex items-center justify-center text-text-light p-6">{err}</div>;
  if (!data) return null;

  const a = data.aggregate || {};
  return (
    <div className="min-h-screen bg-midnight text-text-light">
      <SiteHeader />
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="mb-4"><BackLink to="/dashboard" label="Dashboard" /></div>
        <h1 className="font-serif text-3xl sm:text-4xl text-champagne mb-1 break-words">{data.org.name}</h1>
        <p className="text-text-muted mb-6">Organization dashboard · {data.org.tier} tier</p>

        {orgId && data.auras?.length > 0 && (
          <div className="mb-6">
            <Link
              to={`/auracast/playlist/${orgId}?minutes=5`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-champagne text-midnight text-sm font-medium hover:bg-champagne-light"
              data-testid="run-playlist-btn"
            >
              ▶ Run playlist (all events)
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat icon={Layers} label="Events" value={data.auras?.length || 0} />
          <Stat icon={Image} label="Total media" value={a.total_media || 0} />
          <Stat icon={Users} label="Unique guests" value={a.total_unique_guests || 0} />
          {(data.auras || []).some(au => supportsCapability(au, 'rsvp')) && (
            <Stat icon={Heart} label="RSVPs" value={a.total_rsvps || 0} />
          )}
        </div>

        <h2 className="text-xl font-serif text-champagne mb-3">Mood distribution</h2>
        <div className="flex flex-wrap gap-2 mb-8">
          {Object.entries(a.mood_distribution || {}).map(([m, c]) => (
            <span key={m} className="px-3 py-1 rounded-full bg-white/5 text-sm">{m}: {c}</span>
          ))}
          {!Object.keys(a.mood_distribution || {}).length && <span className="text-text-muted text-sm">No mood tags yet.</span>}
        </div>

        {groups.length > 0 && (
          <>
            <h2 className="text-xl font-serif text-champagne mb-3">Multi-event groups</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {groups.map(g => (
                <Link key={g.id} to={`/group/${g.group_key}`} className="block bg-white/[0.03] rounded-xl border border-white/10 p-4 hover:bg-white/[0.06]">
                  <div className="font-serif text-lg">{g.name}</div>
                  <div className="text-xs text-text-muted mt-1">Group gallery</div>
                </Link>
              ))}
            </div>
          </>
        )}

        <h2 className="text-xl font-serif text-champagne mb-3">Events</h2>
        {data.auras?.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No events yet"
            description="Create an Aura and link it to this organization."
            cta={<Link to="/create-aura" className="btn-champagne inline-flex items-center gap-2">Create event</Link>}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.auras.map(au => (
              <Link key={au.id} to={`/aura/${au.id}`} className="block bg-white/[0.03] rounded-xl border border-white/10 p-4 hover:bg-white/[0.06]">
                <div className="font-serif text-lg">{au.title}</div>
                <div className="text-xs text-text-muted">{formatEventDate(au, { style: 'short' })}</div>
                <div className="text-sm mt-2">{au.media_count} media · {au.is_vaulted ? 'Vaulted' : 'Active'}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <FooterExpanded />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-text-muted text-xs mb-1"><Icon className="w-4 h-4" />{label}</div>
      <div className="text-2xl font-bold text-champagne">{value}</div>
    </div>
  );
}
